/**
 * Connection Detail — Full detail view for MCP servers or API connections
 *
 * MCP Servers have 5 tabs: Overview, Tools, Docker, Metrics, Logs
 * API Connections have 4 tabs: Overview, Endpoints, Auth, Metrics
 *
 * Resolves the connectionId against both MCP and API mock arrays.
 * Back arrow navigates to /connections.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
// Codex retest #266 — `Server` and `XCircle` were imported but never
// rendered. Removed so the icon list reflects what the page actually
// uses.
import {
  ArrowLeft, Plug, Globe, Shield,
  Lock, Unlock, Search,
  Box, Activity, ScrollText, Key, Settings2,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { StatusDot } from '@/components/shared/status-dot';
import { statusToDot, STATUS_BADGE_STYLES } from '@/components/connections/connection-status-badge';
import { CB_BADGE_STYLES } from '@/components/connections/circuit-breaker-badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { CHART_TOOLTIP } from '@/lib/chart-theme';
import {
  TIER_LABELS,
  AUTH_LABELS,
  METHOD_COLORS,
  PERMISSION_LABELS,
  type McpServer,
  type ApiConnection,
  type ConnectionStatus,
} from '@/lib/mock-connections-data';
import {
  useMcpServer,
  useApiConnection,
  useConnectionErrors,
  useToggleApiEndpoint,
  useTestApiConnection,
} from '@/lib/use-connections-module';

export const Route = createFileRoute('/connections/$connectionId')({
  component: ConnectionDetailRoute,
});

// Tiny wrapper: extracts $connectionId from this route's params and
// hands the actual page the explicit prop. Lets the nested-IA aliases
// at /connections/{api,mcp}/$connectionId (round 4 / #228) reuse the
// same ConnectionDetailPage by passing their own `connectionId` value
// without depending on this route's typed param map.
function ConnectionDetailRoute() {
  const { connectionId } = Route.useParams();
  return <ConnectionDetailPage connectionId={connectionId} />;
}

// Status helpers and badge styles imported from @/components/connections/

type McpTab = 'overview' | 'tools' | 'docker' | 'metrics' | 'logs';
type ApiTab = 'overview' | 'endpoints' | 'auth' | 'metrics';

// ── Main Page ──────────────────────────────────────────────────────────────

export function ConnectionDetailPage({
  connectionId,
  expectedType,
}: {
  connectionId: string;
  /**
   * #755 — when the URL is the typed alias (`/connections/mcp/:id` or
   * `/connections/api/:id`) we already know which side to query.
   * Without this hint both hooks fire in parallel and the off-type
   * request returns 404, polluting the browser console + network
   * tab. The unified `/connections/:id` flat route still calls both
   * hooks (no hint, no harm), so we preserve the original behavior
   * by defaulting to `undefined`.
   */
  expectedType?: 'mcp' | 'api';
}) {
  const navigate = useNavigate();

  // #755 — only call the hook that matches the typed alias. The
  // useQuery enabled flag short-circuits the fetch but keeps the
  // component shape stable.
  const mcpEnabled = expectedType !== 'api';
  const apiEnabled = expectedType !== 'mcp';
  const { data: mcpServer } = useMcpServer(connectionId, { enabled: mcpEnabled });
  const { data: apiConnection } = useApiConnection(connectionId, { enabled: apiEnabled });
  const { data: errorLog = [] } = useConnectionErrors();
  const isMcp = !!mcpServer;
  const connection = mcpServer ?? apiConnection;

  // Tab state
  const [mcpTab, setMcpTab] = useState<McpTab>('overview');
  const [apiTab, setApiTab] = useState<ApiTab>('overview');
  const [toolSearch, setToolSearch] = useState('');
  const [endpointSearch, setEndpointSearch] = useState('');

  // Not found
  if (!connection) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Plug className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h2 className="text-lg font-semibold">Connection Not Found</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          No connection found with ID "{connectionId}"
        </p>
        <Button variant="outline" onClick={() => navigate({ to: '/connections' })}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Connections
        </Button>
      </div>
    );
  }

  // Codex retest #157 — only synthesize a chart series when we actually
  // have a current latency reading. Even a flat baseline is misleading
  // (Codex saw a "Latency Trend" chart rendered with 0-4ms axis on a
  // server that explicitly says "No telemetry available yet"). MetricsTab
  // additionally guards: chart card is fully hidden when allNull.
  const currentLatency =
    mcpServer?.metrics.latencyMs ?? apiConnection?.metrics.latencyMs ?? null;
  const latencyData =
    currentLatency != null
      ? Array.from({ length: 12 }, (_, i) => {
          const ts = Date.now() - (12 - i) * 5 * 60 * 1000;
          return {
            time: new Date(ts).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            }),
            latency: currentLatency,
          };
        })
      : [];

  // Error log for this connection
  const connectionErrors = errorLog.filter(e => e.connectionId === connectionId);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate({ to: isMcp ? '/connections' : '/connections/api' })} aria-label="Back to connections" title="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <StatusDot status={statusToDot(connection.status as ConnectionStatus)} size="md" />
          <div className="min-w-0">
            <h1 className="text-lg font-semibold">{connection.displayName}</h1>
            <p className="text-xs text-muted-foreground">{connection.description}</p>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Badge variant="outline" className={cn('text-[9px]', STATUS_BADGE_STYLES[connection.status as ConnectionStatus])}>
              {connection.status}
            </Badge>
            {isMcp && (
              <Badge variant="outline" className="text-[9px]">
                {TIER_LABELS[mcpServer!.tier].label}
              </Badge>
            )}
            {!isMcp && (
              <Badge variant="outline" className="text-[9px]">
                {AUTH_LABELS[apiConnection!.authType]}
              </Badge>
            )}
            <Badge variant="outline" className="text-[9px]">
              {isMcp ? 'MCP' : 'API'}
            </Badge>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 mt-3">
          {isMcp ? (
            <>
              {([
                { id: 'overview', label: 'Overview', icon: Plug },
                { id: 'tools', label: 'Tools', icon: Settings2 },
                { id: 'docker', label: 'Docker', icon: Box },
                { id: 'metrics', label: 'Metrics', icon: Activity },
                { id: 'logs', label: 'Logs', icon: ScrollText },
              ] as { id: McpTab; label: string; icon: typeof Plug }[]).map(tab => (
                <Button
                  key={tab.id}
                  variant={mcpTab === tab.id ? 'secondary' : 'ghost'}
                  size="sm"
                  className="gap-1.5 h-8 text-xs"
                  onClick={() => setMcpTab(tab.id)}
                >
                  <tab.icon className="h-3.5 w-3.5" /> {tab.label}
                </Button>
              ))}
            </>
          ) : (
            <>
              {([
                { id: 'overview', label: 'Overview', icon: Globe },
                { id: 'endpoints', label: 'Endpoints', icon: Settings2 },
                { id: 'auth', label: 'Auth', icon: Key },
                { id: 'metrics', label: 'Metrics', icon: Activity },
              ] as { id: ApiTab; label: string; icon: typeof Globe }[]).map(tab => (
                <Button
                  key={tab.id}
                  variant={apiTab === tab.id ? 'secondary' : 'ghost'}
                  size="sm"
                  className="gap-1.5 h-8 text-xs"
                  onClick={() => setApiTab(tab.id)}
                >
                  <tab.icon className="h-3.5 w-3.5" /> {tab.label}
                </Button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 max-w-5xl space-y-6">
        {isMcp && mcpServer && (
          <>
            {mcpTab === 'overview' && <McpOverview server={mcpServer} />}
            {mcpTab === 'tools' && <McpTools server={mcpServer} search={toolSearch} onSearchChange={setToolSearch} />}
            {mcpTab === 'docker' && <McpDocker server={mcpServer} />}
            {mcpTab === 'metrics' && <MetricsTab data={latencyData} metrics={mcpServer.metrics} />}
            {mcpTab === 'logs' && <LogsTab errors={connectionErrors} />}
          </>
        )}
        {!isMcp && apiConnection && (
          <>
            {apiTab === 'overview' && <ApiOverview connection={apiConnection} />}
            {apiTab === 'endpoints' && <ApiEndpoints connection={apiConnection} search={endpointSearch} onSearchChange={setEndpointSearch} />}
            {apiTab === 'auth' && <ApiAuth connection={apiConnection} />}
            {apiTab === 'metrics' && <MetricsTab data={latencyData} metrics={apiConnection.metrics} />}
          </>
        )}
      </div>
    </div>
  );
}

// ── MCP Tab Components ─────────────────────────────────────────────────────

function McpOverview({ server }: { server: McpServer }) {
  return (
    <>
      {/* Identity card */}
      <Card>
        <CardContent className="pt-4 grid grid-cols-2 gap-4">
          <InfoRow label="Namespace" value={`${server.namespace}.*`} mono />
          <InfoRow label="Tier" value={TIER_LABELS[server.tier].label} />
          <InfoRow label="Endpoint URL" value={server.endpointUrl} mono />
          <InfoRow label="Credentials" value={`${server.credentials.type}${server.credentials.configured ? ' (configured)' : ' (not set)'}`} />
          <InfoRow label="Created" value={new Date(server.createdAt).toLocaleDateString()} />
          <InfoRow label="Updated" value={new Date(server.updatedAt).toLocaleDateString()} />
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniStat label="Tools" value={String(server.toolCount)} />
        <MiniStat label="Avg Latency" value={server.metrics.latencyMs != null ? `${server.metrics.latencyMs}ms` : '—'} />
        <MiniStat label="Uptime" value={server.metrics.uptimePercent != null ? `${server.metrics.uptimePercent}%` : '—'} />
        <MiniStat label="Requests/hr" value={server.metrics.requestsPerHour != null ? server.metrics.requestsPerHour.toLocaleString() : '—'} />
      </div>

      {/* Circuit Breaker */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Circuit Breaker</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoRow label="State" value={server.circuitBreaker.state.replace('_', ' ')} badge badgeClass={CB_BADGE_STYLES[server.circuitBreaker.state]} />
            <InfoRow label="Failures" value={`${server.circuitBreaker.failures}/${server.circuitBreaker.threshold}`} />
            <InfoRow label="Reset Timeout" value={`${server.circuitBreaker.resetTimeout}s`} />
            <InfoRow label="Last Failure" value={server.circuitBreaker.lastFailure ? new Date(server.circuitBreaker.lastFailure).toLocaleString() : 'None'} />
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function McpTools({ server, search, onSearchChange }: { server: McpServer; search: string; onSearchChange: (v: string) => void }) {
  const filtered = server.tools.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
  });

  return (
    <>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search tools..." value={search} onChange={e => onSearchChange(e.target.value)} className="pl-9" />
      </div>

      <div className="rounded-lg border">
        <div className="grid grid-cols-[1fr_80px_90px_80px_80px] gap-3 p-3 bg-muted/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          <span>Tool</span>
          <span>Type</span>
          <span>Permission</span>
          <span className="text-right">Calls</span>
          <span className="text-right">Latency</span>
        </div>
        <div className="divide-y">
          {filtered.map(tool => {
            // Backend tool shape: { name, description, permissionLevel: 'read'|'write', isWriteOperation? }
            // callCount + avgLatencyMs are not yet wired — show '—'.
            const isWrite = tool.isWriteOperation || tool.permissionLevel === 'write';
            const permLabel = PERMISSION_LABELS[tool.permissionLevel] ?? PERMISSION_LABELS.auto_approve;
            return (
              <div key={tool.name} className="grid grid-cols-[1fr_80px_90px_80px_80px] gap-3 p-3 items-center hover:bg-muted/30 transition-colors">
                <div className="min-w-0">
                  <p className="text-xs font-mono font-medium truncate">{tool.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{tool.description}</p>
                </div>
                {isWrite ? (
                  <Badge variant="outline" className="text-[9px] w-fit bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20">
                    <Lock className="h-2.5 w-2.5 mr-0.5" /> Write
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px] w-fit bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                    <Unlock className="h-2.5 w-2.5 mr-0.5" /> Read
                  </Badge>
                )}
                <Badge variant="outline" className={cn('text-[9px] w-fit', permLabel.className)}>
                  {permLabel.label}
                </Badge>
                <p className="text-xs text-right text-muted-foreground">{tool.callCount != null ? tool.callCount.toLocaleString() : '—'}</p>
                <p className="text-xs text-right text-muted-foreground">{tool.avgLatencyMs != null ? `${tool.avgLatencyMs}ms` : '—'}</p>
              </div>
            );
          })}
        </div>
      </div>
      {filtered.length === 0 && search && (
        <p className="text-sm text-muted-foreground text-center py-4">No tools matching "{search}"</p>
      )}
    </>
  );
}

function McpDocker({ server }: { server: McpServer }) {
  const isNative = server.tier === 'tier1';

  if (isNative) {
    return (
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Shield className="h-5 w-5" />
            <div>
              <p className="text-sm font-medium">In-Process Module</p>
              <p className="text-xs text-muted-foreground">Tier 1 IQ Native servers run in-process — no Docker container required.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Docker Sandbox Configuration</CardTitle>
        <CardDescription className="text-xs">Container isolation settings for this MCP server</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="Image" value={server.docker.image} mono />
          <InfoRow label="Tag" value={server.docker.tag} mono />
          <InfoRow label="CPU Limit" value={`${server.docker.cpuLimit} core(s)`} />
          <InfoRow label="Memory Limit" value={server.docker.memoryLimit} />
          <InfoRow label="Network Sandboxed" value={server.docker.sandboxed ? 'Yes — --network=none' : 'No — IP allowlist'} />
          <InfoRow label="PID Limit" value="256" />
          <InfoRow label="FD Limit" value="1024" />
          <InfoRow label="Auto-Restart" value="On failure (max 3)" />
        </div>
      </CardContent>
    </Card>
  );
}

// ── API Tab Components ─────────────────────────────────────────────────────

function ApiOverview({ connection }: { connection: ApiConnection }) {
  const enabledCount = connection.endpoints.filter(ep => ep.enabled).length;

  return (
    <>
      <Card>
        <CardContent className="pt-4 grid grid-cols-2 gap-4">
          <InfoRow label="Base URL" value={connection.baseUrl} mono />
          <InfoRow label="Spec Format" value={connection.specFormat === 'openapi3' ? 'OpenAPI 3.0' : connection.specFormat === 'openapi2' ? 'Swagger 2.0' : 'GraphQL'} />
          <InfoRow label="Auth Type" value={AUTH_LABELS[connection.authType]} />
          <InfoRow label="Auth Status" value={connection.auth.configured ? 'Configured' : 'Not configured'} />
          <InfoRow label="Created" value={new Date(connection.createdAt).toLocaleDateString()} />
          <InfoRow label="Updated" value={new Date(connection.updatedAt).toLocaleDateString()} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniStat label="Active Endpoints" value={String(enabledCount)} />
        <MiniStat label="Total Endpoints" value={String(connection.endpoints.length)} />
        <MiniStat label="Avg Latency" value={connection.metrics.latencyMs != null ? `${connection.metrics.latencyMs}ms` : '—'} />
        <MiniStat label="Uptime" value={connection.metrics.uptimePercent != null ? `${connection.metrics.uptimePercent}%` : '—'} />
      </div>
    </>
  );
}

function ApiEndpoints({ connection, search, onSearchChange }: { connection: ApiConnection; search: string; onSearchChange: (v: string) => void }) {
  // Source of truth = backend; mirror locally only for snappy UI flips.
  const [endpoints, setEndpoints] = useState(connection.endpoints);
  // Re-sync if the backing connection refetches with new data.
  useEffect(() => {
    setEndpoints(connection.endpoints);
  }, [connection.endpoints]);
  const toggleEndpoint = useToggleApiEndpoint();

  const filtered = endpoints.filter(ep => {
    if (!search) return true;
    const q = search.toLowerCase();
    return ep.path.toLowerCase().includes(q) || ep.operationId.toLowerCase().includes(q) || ep.summary.toLowerCase().includes(q);
  });

  const handleToggle = (epId: string) => {
    const ep = endpoints.find(e => e.id === epId);
    if (!ep) return;
    const next = !ep.enabled;
    // Optimistic flip; the mutation invalidates the query and we'll resync.
    setEndpoints(prev => prev.map(e => e.id === epId ? { ...e, enabled: next } : e));
    toggleEndpoint.mutate(
      { connectionId: connection.id, endpointId: epId, enabled: next },
      {
        onSuccess: () =>
          toast.success(`Endpoint ${next ? 'enabled' : 'disabled'} as tool`),
        onError: (err) => {
          // Revert optimistic flip on failure.
          setEndpoints(prev => prev.map(e => e.id === epId ? { ...e, enabled: !next } : e));
          toast.error(`Failed to ${next ? 'enable' : 'disable'} endpoint: ${err.message}`);
        },
      },
    );
  };

  return (
    <>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search endpoints..." value={search} onChange={e => onSearchChange(e.target.value)} className="pl-9" />
      </div>

      <div className="rounded-lg border">
        <div className="grid grid-cols-[70px_1fr_90px_70px_70px_50px] gap-3 p-3 bg-muted/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          <span>Method</span>
          <span>Path</span>
          <span>Category</span>
          <span className="text-right">Calls</span>
          <span className="text-right">Latency</span>
          <span className="text-center">Active</span>
        </div>
        <div className="divide-y">
          {filtered.map(ep => (
            <div key={ep.id} className={cn(
              'grid grid-cols-[70px_1fr_90px_70px_70px_50px] gap-3 p-3 items-center transition-colors',
              ep.enabled ? 'hover:bg-muted/30' : 'opacity-50'
            )}>
              <Badge variant="outline" className={cn('text-[9px] font-mono w-fit', METHOD_COLORS[ep.method])}>
                {ep.method}
              </Badge>
              <div className="min-w-0">
                <p className="text-xs font-mono truncate">{ep.path}</p>
                <p className="text-[11px] text-muted-foreground truncate">{ep.summary}</p>
              </div>
              <Badge variant="secondary" className="text-[9px] w-fit capitalize">{ep.category}</Badge>
              <p className="text-xs text-right">{ep.callCount > 0 ? ep.callCount.toLocaleString() : '—'}</p>
              <p className="text-xs text-right text-muted-foreground">{ep.avgLatencyMs > 0 ? `${ep.avgLatencyMs}ms` : '—'}</p>
              <div className="flex justify-center">
                <Switch checked={ep.enabled} onCheckedChange={() => handleToggle(ep.id)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ApiAuth({ connection }: { connection: ApiConnection }) {
  const testConnection = useTestApiConnection();
  // Codex retest #264 — when the saved auth is OAuth, the backend
  // returns a per-step breakdown (token endpoint / token issued /
  // base URL with token). Surface those rows under the Auth Config
  // card so the admin can see exactly which step failed if
  // re-testing an already-broken setup.
  const [lastSteps, setLastSteps] = useState<
    Array<{ name: string; ok: boolean; message: string; latencyMs: number | null }>
    | null
  >(null);
  const [lastSummary, setLastSummary] = useState<{ ok: boolean; message: string } | null>(null);
  const handleTestAuth = () => {
    const t = toast.loading(
      connection.authType === 'oauth2'
        ? `Probing OAuth for ${connection.displayName}…`
        : `Testing ${connection.displayName}...`,
    );
    setLastSteps(null);
    setLastSummary(null);
    testConnection.mutate(connection.id, {
      onSuccess: (res) => {
        toast.dismiss(t);
        const r = res as typeof res & {
          steps?: Array<{ name: string; ok: boolean; message: string; latencyMs: number | null }>;
        };
        if (r.steps && r.steps.length > 0) setLastSteps(r.steps);
        setLastSummary({ ok: res.ok, message: res.message });
        if (res.ok) toast.success(res.message);
        else toast.error(res.message);
      },
      onError: (err) => {
        toast.dismiss(t);
        toast.error(err.message);
      },
    });
  };
  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Authentication Configuration</CardTitle>
          <CardDescription className="text-xs">How this connection authenticates with the external API</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label="Auth Type" value={AUTH_LABELS[connection.authType]} />
            <InfoRow label="Status" value={connection.auth.configured ? 'Configured' : 'Not configured'} />
            {connection.auth.headerName && <InfoRow label="Header Name" value={connection.auth.headerName} mono />}
            {connection.auth.tokenUrl && <InfoRow label="Token URL" value={connection.auth.tokenUrl} mono />}
            {connection.auth.scopes && connection.auth.scopes.length > 0 && (
              <div className="col-span-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Scopes</p>
                <div className="flex flex-wrap gap-1">
                  {connection.auth.scopes.map(scope => (
                    <Badge key={scope} variant="secondary" className="text-[9px] font-mono">{scope}</Badge>
                  ))}
                </div>
              </div>
            )}
            {/*
              Codex retest #265 — Custom Headers detail visibility.
              The wizard now exposes custom_headers; the backend
              redacts header VALUES but exposes header NAMES via
              `auth.headerNames` so admins can see WHICH headers are
              configured without leaking the values themselves. Shape
              matches the scopes block above.
            */}
            {connection.authType === 'custom_headers' && connection.auth.headerNames && connection.auth.headerNames.length > 0 && (
              <div className="col-span-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Configured Headers</p>
                <div className="flex flex-wrap gap-1">
                  {connection.auth.headerNames.map((name: string) => (
                    <Badge key={name} variant="secondary" className="text-[9px] font-mono">
                      {name} <span className="text-muted-foreground/70 ml-1">••••••</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={handleTestAuth}
        disabled={testConnection.isPending}
      >
        <Key className="h-3.5 w-3.5" />
        {testConnection.isPending ? 'Testing…' : 'Test Authentication'}
      </Button>

      {/*
        Codex retest #264 — Phase Q. Render the OAuth probe's
        per-step breakdown under the Test Authentication button.
        Non-OAuth tests still rely on the toast since their result
        is a single line. Persisted across reloads if the orchestrator
        wires last_test_result.steps into the detail VM later.
      */}
      {lastSteps && lastSteps.length > 0 && (
        <Card className={cn(
          lastSummary?.ok
            ? 'border-emerald-500/20 bg-emerald-500/5'
            : 'border-red-500/20 bg-red-500/5',
        )}>
          <CardContent className="pt-3 pb-3 space-y-2">
            <p className="text-xs font-medium">
              {lastSummary?.ok ? 'OAuth probe summary' : 'OAuth probe failed'}
            </p>
            <ul className="space-y-1.5">
              {lastSteps.map((step, idx) => (
                <li key={`${idx}-${step.name}`} className="flex items-start gap-2 text-xs">
                  {step.ok ? (
                    <CheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
                  )}
                  <div className="min-w-0">
                    <p className={cn(
                      'font-medium',
                      step.ok
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-red-700 dark:text-red-400',
                    )}>
                      {step.name}
                      {step.latencyMs != null && step.latencyMs > 0 && (
                        <span className="ml-1 text-muted-foreground font-normal">({step.latencyMs}ms)</span>
                      )}
                    </p>
                    <p className="text-muted-foreground break-all">{step.message}</p>
                  </div>
                </li>
              ))}
            </ul>
            {lastSummary?.message && (
              <p className="text-[11px] text-muted-foreground pt-1 border-t">
                {lastSummary.message}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

// ── Shared Tab Components ──────────────────────────────────────────────────

function MetricsTab({ data, metrics }: { data: { time: string; latency: number }[]; metrics: { latencyMs: number | null; uptimePercent: number | null; requestsPerHour: number | null; errorRate: number | null } }) {
  const allNull = metrics.latencyMs == null && metrics.uptimePercent == null && metrics.requestsPerHour == null && metrics.errorRate == null;
  return (
    <>
      {allNull ? (
        <Card>
          <CardContent className="pt-6 pb-6 text-center">
            <p className="text-sm text-muted-foreground">
              No telemetry available yet. Metrics will appear after the connection is exercised.
            </p>
          </CardContent>
        </Card>
      ) : null}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniStat label="Avg Latency" value={metrics.latencyMs != null ? `${metrics.latencyMs}ms` : '—'} />
        <MiniStat label="Uptime" value={metrics.uptimePercent != null ? `${metrics.uptimePercent}%` : '—'} />
        <MiniStat label="Requests/hr" value={metrics.requestsPerHour != null ? metrics.requestsPerHour.toLocaleString() : '—'} />
        <MiniStat label="Error Rate" value={metrics.errorRate != null ? `${(metrics.errorRate * 100).toFixed(2)}%` : '—'} />
      </div>

      {/* Codex retest #157: only render the trend card when we actually
          have a series. A flat baseline drawn from `latencyMs ?? 0` is
          misleading next to a "No telemetry available yet" empty state.
          Hide the entire chart card until per-connection telemetry rollup
          ships. */}
      {!allNull && data.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Latency Trend (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} className="text-muted-foreground" />
                <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground" unit="ms" />
                <Tooltip {...CHART_TOOLTIP} />
                <Line type="monotone" dataKey="latency" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function LogsTab({ errors }: { errors: Array<{ id: string; severity: string; timestamp: string; message: string; details?: string }> }) {
  const SEVERITY_STYLES: Record<string, { bg: string; text: string }> = {
    warning: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
    error: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400' },
    critical: { bg: 'bg-red-500/20', text: 'text-red-700 dark:text-red-300' },
  };

  if (errors.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="h-8 w-8 text-emerald-500/30 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No recent errors</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {errors.map(entry => {
        const style = SEVERITY_STYLES[entry.severity] ?? { bg: '', text: '' };
        return (
          <div key={entry.id} className={cn('p-3 rounded-lg', style.bg)}>
            <div className="flex items-center gap-2 mb-0.5">
              <Badge variant="outline" className={cn('text-[9px]', style.text)}>{entry.severity}</Badge>
              <span className="text-[10px] text-muted-foreground">
                {new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <p className="text-xs font-medium">{entry.message}</p>
            {entry.details && <p className="text-[11px] text-muted-foreground mt-0.5">{entry.details}</p>}
          </div>
        );
      })}
    </div>
  );
}

// ── Shared UI Atoms ────────────────────────────────────────────────────────

function InfoRow({ label, value, mono, badge, badgeClass }: { label: string; value: string; mono?: boolean; badge?: boolean; badgeClass?: string }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">{label}</p>
      {badge ? (
        <Badge variant="outline" className={cn('text-[9px]', badgeClass)}>{value}</Badge>
      ) : (
        <p className={cn('text-sm', mono && 'font-mono text-xs')}>{value}</p>
      )}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-3 pb-2 text-center">
        <p className="text-lg font-bold">{value}</p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
